import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';

const ScopeControls = styled.div`
  display: flex;
  align-items: end;
  gap: 10px;
  margin-bottom: 12px;

  @media (max-width: 520px) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const ScopeToggle = styled.div`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, minmax(100px, 1fr));
  gap: 4px;
`;

const ScopeButton = styled.button`
  min-height: 44px;
  padding: 0 16px;
  border: 1px solid ${({ $active }) => ($active ? 'transparent' : 'var(--line-strong)')};
  border-radius: 8px;
  background: ${({ $active }) => ($active ? 'var(--accent)' : 'var(--surface-strong)')};
  color: ${({ $active }) => ($active ? '#f8fbff' : 'var(--accent-deep)')};
  font-weight: 700;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const ScopeField = styled.label`
  flex: 1;
  min-width: 0;
  color: var(--muted);
  font-size: 0.82rem;
  font-weight: 700;

  select {
    display: block;
    width: 100%;
    min-height: 44px;
    margin-top: 4px;
    padding: 0 12px;
    border: 1px solid var(--line);
    border-radius: 8px;
    background: #fdfefe;
    color: var(--text);
  }
`;

export function useLibraryScope(
  searchParams,
  setSearchParams,
  { enabled = true, allowUserScope = false } = {}
) {
  const { can } = useAuth();
  const canManageUsers = can('users:manage');
  const canReadUserSongs = allowUserScope && can('users:songs:read');
  const [bands, setBands] = useState([]);
  const [isLoadingBands, setIsLoadingBands] = useState(enabled);
  const [bandsError, setBandsError] = useState('');
  const [userOwners, setUserOwners] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(canReadUserSongs);
  const [usersError, setUsersError] = useState('');

  useEffect(() => {
    if (!enabled) {
      setBands([]);
      setIsLoadingBands(false);
      setBandsError('');
      return undefined;
    }

    let active = true;
    setIsLoadingBands(true);
    api.getBands().then((data) => {
      if (!active) return;
      setBands((data.bands || []).filter((band) => canManageUsers || band.isMember || band.isOwner));
      setBandsError('');
    }).catch((error) => {
      if (!active) return;
      setBands([]);
      if (error.status !== 401) setBandsError(error.message || 'Band options are unavailable.');
    }).finally(() => {
      if (active) setIsLoadingBands(false);
    });

    return () => { active = false; };
  }, [canManageUsers, enabled]);

  useEffect(() => {
    if (!canReadUserSongs) {
      setUserOwners([]);
      setIsLoadingUsers(false);
      setUsersError('');
      return undefined;
    }

    let active = true;
    setIsLoadingUsers(true);
    api.getSongOwners().then((data) => {
      if (!active) return;
      setUserOwners(data.users || []);
      setUsersError('');
    }).catch((error) => {
      if (!active) return;
      setUserOwners([]);
      if (error.status !== 401) setUsersError(error.message || 'User options are unavailable.');
    }).finally(() => {
      if (active) setIsLoadingUsers(false);
    });

    return () => { active = false; };
  }, [canReadUserSongs]);

  const requestedBandId = searchParams.get('bandId') || '';
  const selectedBand = bands.find((band) => band._id === requestedBandId) || bands[0] || null;
  const selectedBandId = selectedBand?._id || '';
  const requestedUserId = searchParams.get('userId') || '';
  const selectedUser = userOwners.find((user) => user.userId === requestedUserId) || userOwners[0] || null;
  const selectedUserId = selectedUser?.userId || '';
  const requestedScope = searchParams.get('scope');
  const scope = requestedScope === 'band' && selectedBand
    ? 'band'
    : requestedScope === 'user' && canReadUserSongs && selectedUser
      ? 'user'
      : 'personal';
  const libraryApi = useMemo(
    () => scope === 'band'
      ? api.forBand(selectedBandId)
      : scope === 'user'
        ? api.forUser(selectedUserId)
        : api,
    [scope, selectedBandId, selectedUserId]
  );

  const updateScope = (nextScope, ownerId = '') => {
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('scope');
    nextSearchParams.delete('bandId');
    nextSearchParams.delete('userId');
    if (nextScope === 'band' && (ownerId || selectedBandId)) {
      nextSearchParams.set('scope', 'band');
      nextSearchParams.set('bandId', ownerId || selectedBandId);
    } else if (nextScope === 'user' && (ownerId || selectedUserId)) {
      nextSearchParams.set('scope', 'user');
      nextSearchParams.set('userId', ownerId || selectedUserId);
    }
    setSearchParams(nextSearchParams, { replace: true });
  };

  return {
    bands,
    bandsError,
    canReadUserSongs,
    isLoadingScopeOptions: isLoadingBands || isLoadingUsers,
    libraryApi,
    scope,
    selectedBand,
    selectedUser,
    updateScope,
    userOwners,
    usersError,
  };
}

function LibraryScopeFilter({
  bands,
  canReadUserSongs,
  scope,
  selectedBand,
  selectedUser,
  updateScope,
  userOwners,
}) {
  if (!bands.length && !canReadUserSongs) return null;
  const columnCount = 1 + (bands.length ? 1 : 0) + (canReadUserSongs ? 1 : 0);

  return (
    <ScopeControls>
      <ScopeToggle aria-label="Library owner" $columns={columnCount}>
        {bands.length ? (
          <ScopeButton type="button" $active={scope === 'band'} aria-pressed={scope === 'band'}
            onClick={() => updateScope('band')}>
            Band
          </ScopeButton>
        ) : null}
        <ScopeButton type="button" $active={scope === 'personal'} aria-pressed={scope === 'personal'}
          onClick={() => updateScope('personal')}>
          Personal
        </ScopeButton>
        {canReadUserSongs ? (
          <ScopeButton type="button" $active={scope === 'user'} aria-pressed={scope === 'user'}
            disabled={!userOwners.length} onClick={() => updateScope('user')}>
            User
          </ScopeButton>
        ) : null}
      </ScopeToggle>
      {scope === 'band' && bands.length > 1 ? (
        <ScopeField>
          Band
          <select value={selectedBand?._id || ''} onChange={(event) => updateScope('band', event.target.value)}>
            {bands.map((band) => <option key={band._id} value={band._id}>{band.name}</option>)}
          </select>
        </ScopeField>
      ) : null}
      {scope === 'user' && userOwners.length ? (
        <ScopeField>
          User
          <select value={selectedUser?.userId || ''} onChange={(event) => updateScope('user', event.target.value)}>
            {userOwners.map((user) => (
              <option key={user.userId} value={user.userId}>{user.email} ({user.role})</option>
            ))}
          </select>
        </ScopeField>
      ) : null}
    </ScopeControls>
  );
}

export default LibraryScopeFilter;
